<template>
  <div>
    <div
      class="relative group flex items-center gap-3 py-3 cursor-pointer transition-all duration-300"
      @click="toggleDropdown"
      :class="[
        minimized ? 'justify-center px-0' : 'pl-5 pr-6',
        {
          'hover:bg-white/5': !isActive,
          'bg-gradient-to-r from-[#BC13FE] to-[#0A8E97]': isActive,
        }
      ]"
    >
      <div class="flex items-center gap-3 z-10" :class="minimized ? '' : 'flex-1'">
        <div 
          class="w-5 h-5 transition-colors duration-300"
          :style="`
            -webkit-mask-image: url(${icon}); 
            mask-image: url(${icon}); 
            background-color: ${isActive ? '#FFFFFF' : '#B9CACB'}; 
            -webkit-mask-size: contain; 
            mask-size: contain; 
            -webkit-mask-repeat: no-repeat; 
            mask-repeat: no-repeat; 
            -webkit-mask-position: center; 
            mask-position: center;
          `"
        ></div>
        <span 
          v-if="!minimized"
          class="text-sm font-medium transition-colors duration-300"
          :class="isActive ? 'text-white' : 'text-[#B9CACB]'"
        >
          {{ label }}
        </span>
      </div>

      <svg
        v-if="hasDropdown && !minimized"
        :class="['w-4 h-4 transition-transform duration-300', open ? 'rotate-180' : '']"
        fill="none"
        :stroke="isActive ? '#FFFFFF' : '#B9CACB'"
        stroke-width="2"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <Transition
      v-if="!minimized"
      enter-active-class="transition-all duration-300 ease-in-out"
      enter-from-class="opacity-0 max-h-0 scale-y-90"
      enter-to-class="opacity-100 max-h-40 scale-y-100"
      leave-active-class="transition-all duration-300 ease-in-out"
      leave-from-class="opacity-100 max-h-40 scale-y-100"
      leave-to-class="opacity-0 max-h-0 scale-y-90"
    >
      <div
        v-if="open || isDropdownActive"
        class="ml-8 mt-1 space-y-1 text-sm text-gray-300 overflow-hidden"
      >
        <div
          v-for="(item, index) in dropdown"
          :key="index"
          class="cursor-pointer px-2 py-1 ml-1"
          @click="navigateTo(item.value)"
          :class="[
            'cursor-pointer px-2 py-1 ml-1',
            isDropdownItemActive(item)
              ? 'font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#C41BDF] via-[#00D4FF] to-[#00D4FF]/0'
              : 'text-gray-300',
          ]"
        >
          - {{ item.label }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const emit = defineEmits(["click"]);

const props = defineProps({
  icon: String,
  label: String,
  badge: String,
  dropdown: Array,
  path: String,
  identifier: String,
  minimized: Boolean,
});

const route = useRoute();
const router = useRouter();
const open = ref(false);

const hasDropdown = computed(() => props.dropdown && props.dropdown.length > 0);

watch(
  () => route.fullPath,
  () => {
    const isStillInside =
      (props.identifier === "leaderboards" && route.path === "/leaderboards") ||
      (props.identifier === "store" && route.path === "/store");

    open.value = isStillInside;
  }
);

const toggleDropdown = async () => {
  if (hasDropdown.value) {
    if (props.minimized) {
      if (props.identifier === "store") {
        await router.push({ path: "/store", query: { type: "kick" } });
      } else if (props.identifier === "leaderboards") {
        await router.push({ path: "/leaderboards" });
      } else if (props.path) {
        await router.push(props.path);
      }
      return;
    }
    if (props.identifier === "leaderboards") {
      await router.push({ path: "/leaderboards" });
      open.value = true;
    } else if (props.identifier === "store") {
      open.value = !open.value; // ✅ Just toggle the dropdown
    } else {
      open.value = !open.value;
    }
  } else if (props.path) {
    await router.push(props.path);
  } else {
    emit("click");
  }
};

const navigateTo = (value) => {
  if (props.identifier === "store") {
    router.push({ path: "/store", query: { type: value } });
  } else if (props.identifier === "leaderboards") {
    router.push({ path: "/leaderboards", query: { type: value } });
  }
};

const isActive = computed(() => {
  return route.path === props.path || isDropdownActive.value;
});

const isDropdownActive = computed(() => {
  return props.dropdown?.some((item) => isDropdownItemActive(item));
});

const isDropdownItemActive = (item) => {
  if (props.identifier === "leaderboards") {
    const type = item.value?.toLowerCase();
    return route.path === "/leaderboards" && route.query.type === type;
  }

  if (props.identifier === "store") {
    const type = item.value?.toLowerCase();
    return route.path === "/store" && route.query.type === type;
  }

  return false;
};
</script>

<style scoped>
.filter-icon-purple {
  filter: invert(28%) sepia(89%) saturate(7474%) hue-rotate(275deg) brightness(84%)
    contrast(122%);
}
.filter-icon-inactive {
  filter: none;
}
</style>
